import { h, ListView } from "harmaja"
import * as L from "lonna"
import { Board, Connection, Table } from "../../../common/src/domain"
import { EditableSpan } from "../components/EditableSpan"
import { Dispatch } from "../store/board-store"
import { BoardCoordinateHelper } from "./board-coordinates"
import { BoardFocus } from "./board-focus"
import { DragBorder } from "./DragBorder"
import { itemDragToMove } from "./item-dragmove"
import { itemSelectionHandler } from "./item-selection"
import { ToolController } from "./tool-selection"
import { itemZIndex } from "./zIndices"

// Plain-text grid. Click selects the table; when selected, click a cell to edit it.
// ponytail: whole `cells` array is written on each edit (last-write-wins); no per-cell CRDT.
export const TableView = ({
    id,
    table,
    board,
    isLocked,
    focus,
    toolController,
    coordinateHelper,
    latestConnection,
    dispatch,
}: {
    board: L.Property<Board>
    id: string
    table: L.Property<Table>
    isLocked: L.Property<boolean>
    focus: L.Atom<BoardFocus>
    toolController: ToolController
    coordinateHelper: BoardCoordinateHelper
    latestConnection: L.Property<Connection | null>
    dispatch: Dispatch
}) => {
    const { selected, onClick, onTouchStart } = itemSelectionHandler(
        id,
        "table",
        focus,
        toolController,
        board,
        coordinateHelper,
        latestConnection,
        dispatch,
    )
    const setCell = (r: number, c: number, text: string) => {
        const t = table.get()
        const cells = t.cells.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? text : cell)) : row))
        dispatch({ action: "item.update", boardId: board.get().id, items: [{ id, cells }] })
    }
    const editingCells = L.atom(0) // cells in edit mode; wrapper is not draggable while editing (text selection)
    const rowCount = L.view(table, (t) => t.cells.length)
    const colCount = L.view(table, (t) => t.cells[0]?.length ?? 0)

    const Cell = (r: number, c: number) => {
        const value = L.atom(
            L.view(table, (t) => t.cells[r]?.[c] ?? ""),
            (v) => setCell(r, c, v),
        )
        const editing = L.atom(false)
        editing.pipe(L.changes).forEach((e) => editingCells.modify((n) => n + (e ? 1 : -1)))
        return (
            <td>
                <EditableSpan value={value} editingThis={editing} />
            </td>
        )
    }

    return (
        <span
            className={L.view(selected, (s) => (s ? "table selected" : "table"))}
            data-itemid={id}
            draggable={L.view(editingCells, (n) => n === 0)}
            onClick={onClick}
            onTouchStart={onTouchStart}
            ref={
                itemDragToMove(
                    id,
                    board,
                    focus,
                    toolController,
                    coordinateHelper,
                    latestConnection,
                    dispatch,
                    false,
                ) as any
            }
            style={L.view(table, (p) => ({
                top: 0,
                left: 0,
                transform: `translate(${p.x}em, ${p.y}em)`,
                height: p.height + "em",
                width: p.width + "em",
                zIndex: itemZIndex(p),
                position: "absolute",
            }))}
        >
            {L.view(rowCount, colCount, (rows, cols) => (
                <table style={L.view(table, (t) => ({ fontSize: (t.fontSize ?? 1.2) + "em" }))}>
                    <tbody>
                        {Array.from({ length: rows }, (_, r) => (
                            <tr>{Array.from({ length: cols }, (_, c) => Cell(r, c))}</tr>
                        ))}
                    </tbody>
                </table>
            ))}
            {L.view(isLocked, (l) => l && <span className="lock">🔒</span>)}
            <DragBorder {...{ id, board, toolController, coordinateHelper, latestConnection, focus, dispatch }} />
        </span>
    )
}
